var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { customElement, property } from 'lit/decorators.js';
import { html } from 'lit';
import { updateWhenLocaleChanges } from '@lit/localize';
import { formatDate, parseDate } from 'devextreme/localization';
import { v4 as uuid4 } from 'uuid';
import DropDownBox from 'devextreme/ui/drop_down_box';
import TextBox from 'devextreme/ui/text_box';
import { DateHelper } from '../../helpers/index';
import { DinapsisDateRangeBase } from './dinapsis-date-range-base';
import { DinapsisDateRangeCalendar } from './dinapsis-date-range-calendar';
import { DinapsisDateRangeIntervals } from './dinapsis-date-range-intervals';
/**
 * Represents the dinapsis-date-range componente, that allow select a date interval.
 */
let DinapsisDateRange = class DinapsisDateRange extends DinapsisDateRangeBase {
    constructor() {
        super();
        /**
         * A property that is used to set the id of the div that the date-range is rendered in.
         */
        this.dateRangeId = 'dinapsis-date-range';
        /**
         * Array that contents predefined intervals
         */
        this.defaultInterval = -1;
        /**
         * Dropdown component
         */
        this.dropdown = undefined;
        /**
         * TextBox subcomponent
         */
        this.textBox = undefined;
        /**
         * Private variable for set random id
         */
        this.randomId = uuid4();
        /**
         * Generate text box template for dropdown box.
         * @param _value Value
         * @param fieldElement Parent element where insert text box.
         */
        this.fieldTemplate = (_value, fieldElement) => {
            const textBoxContainer = document.getElementById(`${this.dateRangeId}-${this.randomId}`);
            const div = document.createElement('div');
            if (textBoxContainer) {
                this.textBox = new TextBox(div);
                this.textBox.option('readOnly', true);
                this.textBox.option('showClearButton', false);
                this.textBox.option('value', this.getDateRangeFormattedText());
                this.textBox.option('onValueChanged', this.onValueChangedInTextBox);
                fieldElement.append(div);
            }
        };
        /**
         * Return formatted string with de date range.
         * E.g: 'dd/MM/yyyy - dd/MM/yyyy' in Spanish locale.
         */
        this.getDateRangeFormattedText = () => {
            const start = `${formatDate(new Date(this.startDate), 'shortDate')}`;
            const end = `${formatDate(new Date(this.endDate), 'shortDate')}`;
            return `${start} - ${end}`;
        };
        /**
         * Return HTMLElement that is show when dropdown is opened.
         * @param _value Value of dropdown TextBox
         * @param container Container
         * @returns HTMLElement that is show when dropdown is opened.
         */
        this.contentTemplate = (_value, container) => {
            const div = document.createElement('div');
            div.className = 'date-range-intervals-container';
            if (this.intervals.length > 0) {
                const defaultRanges = this.createDefaultRanges();
                div.appendChild(defaultRanges);
            }
            const calendar = this.createCalendar();
            div.appendChild(calendar);
            container.appendChild(div);
            return div;
        };
        /**
         * Handler when dates changed in intervals radio group.
         * @param e Event
         */
        this.onDatesChangedInIntervals = (e) => {
            var _a;
            this.calendar.startDate = this.startDate;
            this.calendar.endDate = this.endDate;
            this.startDate = e.detail.startDate;
            this.endDate = e.detail.endDate;
            this.defaultInterval = e.detail.intervalPosition;
            (_a = this.textBox) === null || _a === void 0 ? void 0 : _a.option('value', this.getDateRangeFormattedText());
        };
        /**
         * Handler when dates changed in text box.
         * @param e Event.
         */
        this.onValueChangedInTextBox = (e) => {
            var _a;
            try {
                const range = e.value.split('-');
                const start = DateHelper.getYearMonthDayString(parseDate(range[0].trim(), 'shortDate'));
                const end = DateHelper.getYearMonthDayString(parseDate(range[1].trim(), 'shortDate'));
                this.startDate = start;
                this.endDate = end;
                this.calendar.startDate = start;
                this.calendar.endDate = end;
                if (start !== end) {
                    this.emitDatesChangedEvent();
                    (_a = this.dropdown) === null || _a === void 0 ? void 0 : _a.close();
                }
            }
            catch (error) {
                console.error('Invalid date range:', e.value);
            }
        };
        /**
         * Handler when dates changed in calendar.
         * @param e Event.
         */
        this.onDatesChangedInCalendar = (e) => {
            var _a;
            this.startDate = e.detail.startDate;
            this.endDate = e.detail.endDate;
            (_a = this.textBox) === null || _a === void 0 ? void 0 : _a.option('value', this.getDateRangeFormattedText());
            if (e.detail.intervalPosition !== null) {
                this.interval.resetSelectedOption();
            }
        };
        const now = DateHelper.getYearMonthDayString(new Date());
        this.startDate = now;
        this.endDate = now;
        this.label = '';
        this.width = '240px';
        this.opened = false;
        this.condensed = false;
        this.interval = new DinapsisDateRangeIntervals();
        this.calendar = new DinapsisDateRangeCalendar();
        this.intervals = [];
        updateWhenLocaleChanges(this);
    }
    /**
     * The above function is a lifecycle hook that is called when the component
     * is first updated. It is used to initialize the date-range view.
     */
    firstUpdated() {
        const dateRangeContainer = document.getElementById(`${this.dateRangeId}-${this.randomId}`);
        this.interval.intervalSelected = this.defaultInterval;
        if (this.defaultInterval > -1) {
            const interval = this.intervals[this.defaultInterval];
            if (interval.backIntervalFromToday) {
                const result = DateHelper.getDateFromBackIntervalFromToday(interval.backIntervalFromToday);
                this.startDate = result.startDate;
                this.endDate = result.endDate;
            }
            else {
                this.startDate = interval.startDate;
                this.endDate = interval.endDate;
            }
        }
        if (dateRangeContainer) {
            // Instance Creation
            this.dropdown = this.createDropDown(dateRangeContainer);
        }
    }
    /**
     * If any property has changed when finish update this method is called an
     * depending on wich property has been modified,
     * it executes its update method
     * @param changedProperties - Map<string, unknown>
     */
    updated(changedProperties) {
        var _a;
        if (changedProperties.has('startDate') || changedProperties.has('endDate')) {
            (_a = this.textBox) === null || _a === void 0 ? void 0 : _a.option('value', this.getDateRangeFormattedText());
        }
        this.interval.intervalSelected = this.defaultInterval;
    }
    /**
     * Creates the dropdown box.
     * @param container Parent container.
     * @returns Dropdown
     */
    createDropDown(container) {
        return new DropDownBox(container, {
            label: this.label,
            width: this.width,
            opened: this.opened,
            height: `${this.condensed ? '48px' : '56px'}`,
            openOnFieldClick: false,
            stylingMode: 'outlined',
            placeholder: '  /  /     -   /  /   ',
            dropDownButtonTemplate: '<div><span class="material-icons-round event-icon">event</span></div>',
            value: `${this.getDateRangeFormattedText()}`,
            fieldTemplate: this.fieldTemplate,
            contentTemplate: this.contentTemplate,
        });
    }
    /**
     * Sets properties and event listener of DinapsisDateRangeIntervals.
     * @returns DinapsisDateRangeIntervals component.
     */
    createDefaultRanges() {
        this.interval.intervalsId = `${this.dateRangeId}-interval-${this.randomId}`;
        this.interval.intervals = this.intervals;
        this.interval.addEventListener('dates-changed', this.onDatesChangedInIntervals);
        return this.interval;
    }
    /**
     * Sets properties and event listener of DinapsisDateRangeCalendar.
     * @returns DinapsisDateRangeCalendar component.
     */
    createCalendar() {
        this.calendar.calendarId = `${this.dateRangeId}-calendar-${this.randomId}`;
        this.calendar.startDate = `${this.startDate}`;
        this.calendar.endDate = `${this.endDate}`;
        this.calendar.addEventListener('dates-changed', this.onDatesChangedInCalendar);
        return this.calendar;
    }
    /**
     * Emits the change event with the start and end date.
     */
    emitDatesChangedEvent() {
        const emitDates = {
            startDate: this.startDate,
            endDate: this.endDate,
            intervalPosition: this.interval.intervalSelected,
        };
        this.dispatchEvent(new CustomEvent('dates-changed', { detail: emitDates }));
    }
    /**
     * The createRenderRoot function is a function that is called by the lit-html library to tell the
     * component where to render the html.
     * @returns The element itself.
     */
    createRenderRoot() {
        return this;
    }
    /**
     * Renders the template literal HTML of the component.
     * @returns The HTML template literal.
     */
    render() {
        return html `
        <div
          style="display: ${this.width === 'auto' ? 'block' : 'inline-block'}"
          class="date-range-container ${this.condensed ? 'condensed' : ''}"
          id="${this.dateRangeId}-${this.randomId}"
        >
        </div>
    `;
    }
};
__decorate([
    property({ type: String })
], DinapsisDateRange.prototype, "label", void 0);
__decorate([
    property({ type: String })
], DinapsisDateRange.prototype, "dateRangeId", void 0);
__decorate([
    property({ type: String })
], DinapsisDateRange.prototype, "width", void 0);
__decorate([
    property({ type: Boolean })
], DinapsisDateRange.prototype, "opened", void 0);
__decorate([
    property({ type: Boolean })
], DinapsisDateRange.prototype, "condensed", void 0);
__decorate([
    property({ type: Array() })
], DinapsisDateRange.prototype, "intervals", void 0);
__decorate([
    property({ type: Number })
], DinapsisDateRange.prototype, "defaultInterval", void 0);
DinapsisDateRange = __decorate([
    customElement('dinapsis-date-range')
], DinapsisDateRange);
export { DinapsisDateRange };
//# sourceMappingURL=dinapsis-date-range.js.map